import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext.tsx';
import { useTenant } from '@/context/TenantContext.tsx';
import { FileText, LogOut, User } from 'lucide-react';

/**
 * EmployeeLayout — shell simplificado para usuarios con rol 'employee'.
 * Sin sidebar de administración: solo cabecera y menú mínimo.
 */
export function EmployeeLayout() {
  const { user, logout } = useAuth();
  const { tenantSlug }   = useTenant();

  const base = `/${tenantSlug ?? ''}`;

  const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.875rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    textDecoration: 'none',
    color:      isActive ? 'var(--color-primary)' : 'var(--color-muted)',
    background: isActive ? 'color-mix(in srgb, var(--color-primary) 10%, transparent)' : 'transparent',
    transition: 'background 0.15s, color 0.15s',
  } as React.CSSProperties);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: 'var(--color-bg)',
      fontFamily: 'var(--font-sans, system-ui, sans-serif)',
    }}>
      {/* Cabecera */}
      <header style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 1.5rem',
        height: '56px',
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}>
        {/* Logo / nombre empresa */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <div style={{
            width: 28, height: 28, borderRadius: 6,
            background: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>
              {(tenantSlug ?? 'E').charAt(0).toUpperCase()}
            </span>
          </div>
          <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text)' }}>
            Portal del Empleado
          </span>
        </div>

        {/* Navegacion */}
        <nav style={{ display: 'flex', gap: '0.25rem' }}>
          <NavLink to={`${base}/mis-nominas`} style={navLinkStyle}>
            <FileText size={15} /> Mis Nominas
          </NavLink>
        </nav>

        {/* Usuario + logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'color-mix(in srgb, var(--color-primary) 15%, transparent)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <User size={14} style={{ color: 'var(--color-primary)' }} />
            </div>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text)', fontWeight: 500 }}>
              {user?.name ?? user?.email ?? 'Empleado'}
            </span>
          </div>
          <button
            onClick={logout}
            title="Cerrar sesion"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.3rem 0.75rem', borderRadius: 6,
              border: '1px solid var(--color-border)',
              background: 'transparent',
              color: 'var(--color-muted)',
              cursor: 'pointer', fontSize: '0.8125rem',
              fontFamily: 'inherit',
            }}
          >
            <LogOut size={13} /> Salir
          </button>
        </div>
      </header>

      {/* Contenido principal */}
      <main style={{
        flex: 1,
        maxWidth: 900,
        width: '100%',
        margin: '0 auto',
        padding: '2rem 1.5rem',
      }}>
        <Outlet />
      </main>
    </div>
  );
}
